/**
 * Health Store - Data-Driven Connection Resilience
 *
 * ISSUE_045: Data Flow Freshness 기반 연결 상태 관리
 *
 * 핵심 원칙:
 * - WebSocket liveness가 아닌 Data Flow Freshness 측정
 * - lastRealtimeEventAt, lastQueryOkAt, lastSyncOkAt 세 타임스탬프 기반
 * - Active probe는 cooldown과 함께 안전하게 실행
 *
 * @module core/resilience/healthStore
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

// =============================================================================
// TYPES
// =============================================================================

export type HealthStatus = 'connected' | 'degraded' | 'disconnected';

/** ISSUE_073: UI 4-State 계약 */
export type UIConnectionStatus = 'ok' | 'syncing' | 'stale_realtime' | 'offline';

export interface DataFreshnessState {
  status: HealthStatus;

  // Data flow timestamps (epoch ms)
  lastRealtimeEventAt: number;
  lastQueryOkAt: number;
  lastSyncOkAt: number;

  // ISSUE_073: WS heartbeat timestamp
  lastWsHeartbeatAt: number;

  // ISSUE_073: UI 상태 (evaluateState에서 갱신 — Zustand selector 안정성)
  uiStatus: UIConnectionStatus;

  // Active probe control
  lastProbeAt: number;
  consecutiveProbeFailures: number;

  // UI debounce
  statusChangedAt: number;
  pendingStatus: HealthStatus | null;
}

export interface DataFreshnessActions {
  // Timestamp updates
  touchRealtime(): void;
  touchQueryOk(): void;
  touchSyncOk(): void;

  // ISSUE_073: WS heartbeat
  touchWsHeartbeat(): void;
  setUIStatus(status: UIConnectionStatus): void;

  // Probe control
  markProbeSuccess(): void;
  markProbeFailure(): void;
  canProbe(): boolean;

  // Status management
  setStatus(status: HealthStatus): void;
  setPendingStatus(status: HealthStatus | null): void;
  getLastActivity(): number;

  // Computed
  getDelta(): number;
  shouldProbe(): boolean;
}

type HealthStore = DataFreshnessState & DataFreshnessActions;

// =============================================================================
// CONSTANTS
// =============================================================================

/** CONNECTED -> DEGRADED 임계값 (3분) */
export const DEGRADED_THRESHOLD_MS = 3 * 60 * 1000;

/** DEGRADED -> DISCONNECTED 임계값 (10분) */
export const DISCONNECTED_THRESHOLD_MS = 10 * 60 * 1000;

/** Probe 쿨다운 (1분) - 과도한 probe 방지 */
export const PROBE_COOLDOWN_MS = 60 * 1000;

/** Probe 타임아웃 (5초) */
export const PROBE_TIMEOUT_MS = 5 * 1000;

/** UI 상태 변경 디바운스 (5초) - 빠른 상태 변경 시 UI 깜빡임 방지 */
export const STATUS_DEBOUNCE_MS = 5 * 1000;

/** Probe 실패 임계값 - 이 횟수 이상 실패 시 Recovery 시작 */
export const PROBE_FAILURE_THRESHOLD = 2;

/** ISSUE_073: WS heartbeat가 이 시간 이상 stale이면 stale_realtime UI 상태 (90초 = 3회 연속 미수신) */
export const WS_STALE_THRESHOLD_MS = 90 * 1000;

// =============================================================================
// STORE
// =============================================================================

const now = Date.now();

const initialState: DataFreshnessState = {
  status: 'connected',

  // 초기값: 현재 시간 (앱 시작 시 connected로 간주)
  lastRealtimeEventAt: now,
  lastQueryOkAt: now,
  lastSyncOkAt: now,

  // ISSUE_073: WS heartbeat (초기값: 현재 시간)
  lastWsHeartbeatAt: now,
  uiStatus: 'ok',

  // Probe 제어
  lastProbeAt: 0,
  consecutiveProbeFailures: 0,

  // UI 디바운스
  statusChangedAt: now,
  pendingStatus: null,
};

export const useHealthStore = create<HealthStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      ...initialState,

      // =========================================================================
      // TIMESTAMP UPDATES
      // =========================================================================

      touchRealtime: () => {
        const now = Date.now();
        set(
          { lastRealtimeEventAt: now },
          false,
          'touchRealtime'
        );
      },

      touchQueryOk: () => {
        const now = Date.now();
        set(
          { lastQueryOkAt: now },
          false,
          'touchQueryOk'
        );
      },

      touchSyncOk: () => {
        const now = Date.now();
        set(
          { lastSyncOkAt: now },
          false,
          'touchSyncOk'
        );
      },

      // ISSUE_073: WS heartbeat
      touchWsHeartbeat: () => {
        set({ lastWsHeartbeatAt: Date.now() }, false, 'touchWsHeartbeat');
      },

      setUIStatus: (status: UIConnectionStatus) => {
        const current = get().uiStatus;
        if (current === status) return;
        set({ uiStatus: status }, false, `setUIStatus:${status}`);
      },

      // =========================================================================
      // PROBE CONTROL
      // =========================================================================

      markProbeSuccess: () => {
        const now = Date.now();
        set(
          {
            lastQueryOkAt: now,
            lastProbeAt: now,
            consecutiveProbeFailures: 0,
          },
          false,
          'markProbeSuccess'
        );
      },

      markProbeFailure: () => {
        const state = get();
        const now = Date.now();
        const newFailures = state.consecutiveProbeFailures + 1;
        set(
          {
            lastProbeAt: now,
            consecutiveProbeFailures: newFailures,
          },
          false,
          'markProbeFailure'
        );
      },

      canProbe: () => {
        const state = get();
        const now = Date.now();
        return now - state.lastProbeAt >= PROBE_COOLDOWN_MS;
      },

      shouldProbe: () => {
        const state = get();
        if (state.status !== 'degraded') return false;
        if (state.consecutiveProbeFailures >= PROBE_FAILURE_THRESHOLD) return false;
        return state.canProbe();
      },

      // =========================================================================
      // STATUS MANAGEMENT
      // =========================================================================

      setStatus: (status: HealthStatus) => {
        const state = get();
        if (state.status === status) return;

        const now = Date.now();
        set(
          {
            status,
            statusChangedAt: now,
            pendingStatus: null,
            // M2: connected 전이 시 probe 실패 카운터 리셋 — 교착 방지
            ...(status === 'connected' ? { consecutiveProbeFailures: 0 } : {}),
          },
          false,
          `setStatus:${status}`
        );

      },

      setPendingStatus: (status: HealthStatus | null) => {
        set({ pendingStatus: status }, false, 'setPendingStatus');
      },

      getLastActivity: () => {
        const state = get();
        return Math.max(
          state.lastRealtimeEventAt,
          state.lastQueryOkAt,
          state.lastSyncOkAt
        );
      },

      getDelta: () => {
        const state = get();
        return Date.now() - state.getLastActivity();
      },
    })),
    { name: 'health-store' }
  )
);

// =============================================================================
// SELECTORS
// =============================================================================

export const selectHealthStatus = (state: HealthStore) => state.status;
export const selectIsConnected = (state: HealthStore) => state.status === 'connected';
export const selectIsDegraded = (state: HealthStore) => state.status === 'degraded';
export const selectIsDisconnected = (state: HealthStore) => state.status === 'disconnected';

/**
 * ISSUE_073: UI 4-State 계약
 *
 * | 상태 | 조건 | UI |
 * |------|------|-----|
 * | ok | 모든 센서 정상 | 숨김 또는 녹색 점 |
 * | syncing | (Phase 4 예약) | 파란 점 |
 * | stale_realtime | WS heartbeat > 90초 stale | 앰버 배너 |
 * | offline | navigator.onLine === false | 빨간 배너 |
 *
 * evaluateState()에서 갱신된 필드를 참조 — Date.now() 없이 순수 필드 비교
 */
export const selectUIStatus = (state: HealthStore): UIConnectionStatus => state.uiStatus;
